import { Check, ChevronRight, Link2 } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { DecisionStamp, stampText } from 'common-src/components/base';
import { getDashboardClients } from 'common-src/models/Client';

import useCustomSelector from 'src/hooks/useCustomSelector';

import { TASK_PRIORITY } from '../../constants';
import {
  formatDue,
  getTaskDisplayTitle,
  isLinkedTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
  isTaskReadinessComplete,
} from '../../helpers';
import { Badge } from '../Badge';
import styles from './HuddleTaskRow.module.scss';

const HuddleTaskRow = ({ task, isMarking, isAssigning, onMarkComplete, onAssignToMe, onOpen }) => {
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const isDone = isTaskReadinessComplete(task);
  const doneBy = clients.find((client) => client?.id === task?.updatedBy);
  const isBlocked = isTaskBlockedStatus(task);

  const isLinked = !isDone && isLinkedTask(task);
  const due = task?.due ? formatDue(task.due) : null;
  const description =
    `${task?.taskDescription || task?.task_description || task?.caption || ''}`.trim();
  const ownerLabel = `${task?.owner || ''}`.trim() || 'Unassigned';

  const canAssignToMe = !isDone && ownerLabel.toLowerCase() === 'unassigned';

  const isUrgent =
    !isDone &&
    (task?.priority === TASK_PRIORITY.Critical ||
      task?.priority === TASK_PRIORITY.High ||
      isBlocked ||
      isTaskEscalatedStatus(task));

  return (
    <div className={styles.row}>
      {isLinked ? (
        <span className={styles.linkedIcon} aria-hidden="true">
          <Link2 className={styles.linkedIconGlyph} strokeWidth={1.8} />
        </span>
      ) : (
        <button
          type="button"
          role="checkbox"
          aria-checked={isDone}
          disabled={isDone || isMarking}
          className={`${styles.checkbox} ${isDone ? styles.checkboxChecked : ''}`}
          onClick={() => onMarkComplete(task.id)}
        >
          {isDone && (
            <Check size={16} strokeWidth={3} className={styles.checkmark} aria-hidden="true" />
          )}
        </button>
      )}

      <button type="button" className={styles.label} onClick={() => onOpen(task)}>
        <span className={styles.labelText}>
          <span
            className={`${styles.title} ${isDone ? styles.titleDone : ''} ${
              isUrgent ? styles.titleUrgent : ''
            }`}
          >
            {getTaskDisplayTitle(task)}
          </span>
          {description && (
            <span className={`${styles.description} ${isDone ? styles.descriptionDone : ''}`}>
              {description}
            </span>
          )}
          <span className={styles.facts}>
            <span>{ownerLabel}</span>
            {due && (
              <>
                <span aria-hidden="true">·</span>
                <span className={due.urgent && !isDone ? styles.dueUrgent : undefined}>
                  Due {due.text}
                </span>
              </>
            )}
          </span>
          {isDone && (
            <DecisionStamp
              label="Done"
              tone="success"
              stamp={stampText({
                where: 'Action Area',
                by: doneBy?.getName?.(false, true),
                at: task?.updatedAt,
              })}
            />
          )}
        </span>
        <ChevronRight size={18} className={styles.openChevron} aria-hidden="true" />
      </button>

      <div className={styles.actions}>
        {isBlocked && <Badge variant="critical">Blocked</Badge>}
        {canAssignToMe && (
          <button
            type="button"
            className={styles.assignButton}
            disabled={isAssigning}
            onClick={(event) => {
              event.stopPropagation();
              onAssignToMe(task.id);
            }}
          >
            {isAssigning ? 'Assigning…' : 'Assign to me'}
          </button>
        )}
      </div>
    </div>
  );
};

HuddleTaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    caption: PropTypes.string,
    taskDescription: PropTypes.string,
    task_description: PropTypes.string,
    owner: PropTypes.string,
    due: PropTypes.string,
    blockedStatus: PropTypes.number,
    priority: PropTypes.string,
    updatedBy: PropTypes.number,
    updatedAt: PropTypes.string,
  }).isRequired,
  isMarking: PropTypes.bool,
  isAssigning: PropTypes.bool,
  onMarkComplete: PropTypes.func.isRequired,
  onAssignToMe: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default HuddleTaskRow;
